import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { Pagination } from '../../components/ui/Pagination';
import { MobileFilters } from '../../components/ui/MobileFilters';
import { OrdinaFiltra, useOrdina, type OpzioneOrdina } from '../../components/ui/OrdinaFiltra';
import { useUrlState } from '../../hooks/useUrlState';
import { fmtEur, fmtDate } from '../../lib/utils';
import { incassiQuery, type IncassoRow } from './adminApi';
import { useAdminActions } from './useAdminActions';
import { IGNORE_REASON_LABEL } from '@masamune/shared';
import { METODO_LABEL, etichettaRata, rataInParole } from './fatture/fattureApi';

/**
 * Fatture › le due liste "di servizio" che si aprono dal menu "Altre fatture"
 * di Fatture da incassare (`?status=`):
 *
 *   incassate  Fatture già incassate: le rate incassate, la più recente in
 *              cima, con "Rimetti da incassare" se l'incasso era sbagliato.
 *              Una rata segnata qui e non ancora su FattureInCloud lo dice.
 *   cestinate  Fatture tolte dall'elenco (era il "cestino"): una riga per
 *              fattura, con motivo e data, e "Rimetti nell'elenco". Togliere
 *              non annulla niente su FattureInCloud.
 *
 * La lista delle fatture aperte è FattureDaIncassare (fatture/): questo file
 * resta per queste due, che sono tabelle semplici paginate dall'API.
 */
const PAGE_SIZE = 25;

/** Ordina per (24/9), solo in Fatture già incassate: la lista è paginata, ordina l'API. */
const ORDINI_INCASSATE: OpzioneOrdina[] = [
  { key: 'recenti', label: 'Incassate più di recente prima' },
  { key: 'vecchie', label: 'Incassate da più tempo prima' },
  { key: 'importo', label: 'Prima gli importi più alti' },
  { key: 'cliente', label: 'Cliente dalla A alla Z' },
];

export function IncassiTab({ status, canWrite, showHistory, onBack, tabs }: {
  status: 'incassate' | 'cestinate';
  canWrite: boolean;
  showHistory?: boolean;
  onBack: () => void;
  /** Le linguette della pagina: stanno sopra la barra dei filtri (mobile.md 4bis.4). */
  tabs?: ReactNode;
}) {
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [page, setPage] = useState(1);
  const ordina = useOrdina(ORDINI_INCASSATE);
  const sort = status === 'incassate' ? ordina.key : undefined;
  useEffect(() => { setPage(1); }, [status, search, showHistory, sort]);

  const params = useMemo(
    () => ({ status, search: search.trim() || undefined, sort, includeHistory: showHistory, page, pageSize: PAGE_SIZE }),
    [status, search, sort, showHistory, page],
  );
  const q = useQuery(incassiQuery(params));
  const items = q.data?.items ?? [];
  const tolte = status === 'cestinate';
  const titolo = tolte ? "Fatture tolte dall'elenco" : 'Fatture già incassate';
  const showRata = !tolte && items.some((r) => rataInParole(r.seq, r.ofTotal));

  const nota = (
    <div className="adm-note">
      <Icon name={tolte ? 'trash' : 'check'} size={12} className="mute" />
      <span>
        {tolte
          ? <>Qui ci sono le fatture tolte dall'elenco: non entrano nei totali, ma <strong>su FattureInCloud restano</strong>. Per annullarne una davvero serve una nota di credito.</>
          : <>Qui ci sono le rate già incassate{ordina.predefinita ? ', la più recente in cima' : `, ${ordina.attiva.label.toLowerCase()}`}.</>}
        {' '}<a className="underline cursor-pointer" onClick={onBack}>Torna alle fatture da incassare</a>
      </span>
    </div>
  );

  return (
    <>
      {tabs}

      <MobileFilters attivi={search.trim() ? 1 : 0} label={tolte ? 'Filtri' : 'Cerca e ordina'}>
        <div className="filterbar filterbar--float mb-4 flex-wrap">
          <div className="tb-search flex-1" style={{ minWidth: 200, margin: 0 }}>
            <Icon name="search" size={12} />
            <input aria-label={`Cerca fra le ${titolo.toLowerCase()}`} placeholder="Cerca per numero di fattura o cliente…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <span className="mute text-[12px]">
            {tolte
              ? ((q.data?.total ?? 0) === 1 ? "1 fattura tolta dall'elenco" : `${q.data?.total ?? 0} fatture tolte dall'elenco`)
              : ((q.data?.total ?? 0) === 1 ? '1 rata incassata' : `${q.data?.total ?? 0} rate incassate`)}
          </span>
          {!tolte && <OrdinaFiltra ordina={ordina} />}
          <button className="btn btn-sm btn-ghost" onClick={onBack}>
            <Icon name="arrowLeft" size={10} /> Fatture da incassare
          </button>
        </div>
      </MobileFilters>

      {nota}

      {q.isLoading && !q.data ? (
        <div className="ed-section"><table className="tbl tbl--ed"><tbody><SkeletonTable rows={6} cols={5} withAvatar={false} /></tbody></table></div>
      ) : items.length === 0 ? (
        search.trim() ? (
          <EmptyState brand title={`Nessuna fra le ${titolo.toLowerCase()} con questa ricerca`} description="Prova con il numero della fattura o il nome del cliente."
            action={<button className="btn" onClick={() => setSearch('')}>Azzera la ricerca</button>} />
        ) : tolte ? (
          <EmptyState brand title="Nessuna fattura tolta dall'elenco" description="Qui finiscono le fatture che nell'elenco non devono stare: quelle rifiutate dall'Agenzia delle Entrate, i doppioni, gli errori. Si possono sempre rimettere." />
        ) : (
          <EmptyState brand title="Nessuna rata incassata" description="Le rate incassate compaiono qui, dopo l'aggiornamento da FattureInCloud o quando le segni con «Segna incassata»." />
        )
      ) : (
        <div className="ed-section">
          <div className="data-hd"><span className="t-micro">{titolo}</span></div>
          <table className="tbl tbl--ed tbl--card tbl--static">
            {tolte ? (
              <>
                <thead>
                  <tr>
                    <th>Cliente e fattura</th>
                    <th style={{ width: 150 }}>Tolta il</th>
                    <th>Perché è stata tolta</th>
                    <th className="num" style={{ width: 130 }}>Importo</th>
                    <th style={{ width: 190 }} />
                  </tr>
                </thead>
                <tbody>{items.map((r) => <RigaTolta key={r.id} r={r} canWrite={canWrite} />)}</tbody>
              </>
            ) : (
              <>
                <thead>
                  <tr>
                    <th>Cliente e fattura</th>
                    {showRata && <th style={{ width: 120 }}>Rata</th>}
                    <th style={{ width: 150 }}>Incassata il</th>
                    <th style={{ width: 190 }}>Pagata con</th>
                    <th className="num" style={{ width: 130 }}>Importo</th>
                    <th style={{ width: 200 }} />
                  </tr>
                </thead>
                <tbody>{items.map((r) => <RigaIncassata key={r.id} r={r} canWrite={canWrite} showRata={showRata} />)}</tbody>
              </>
            )}
          </table>
          {q.data && <Pagination page={page} pageSize={q.data.pageSize} total={q.data.total} totalPages={q.data.totalPages} onPage={setPage} />}
        </div>
      )}
    </>
  );
}

function ClienteFattura({ r }: { r: IncassoRow }) {
  return (
    <>
      <div className="font-semibold">{r.client.shortName}</div>
      <div className="text-[12px] mute">Fattura <span className="mono">{r.invoiceNumber}</span></div>
    </>
  );
}

function RigaIncassata({ r, canWrite, showRata }: { r: IncassoRow; canWrite: boolean; showRata: boolean }) {
  const { unmarkPaid } = useAdminActions();
  const rata = rataInParole(r.seq, r.ofTotal);
  const stornata = r.status === 'STORNATA';
  return (
    <tr style={{ cursor: 'default' }}>
      <td data-m="hd">
        <ClienteFattura r={r} />
        {r.localPaidAt && (
          <div className="fs-sugg">
            <Chip tone="ghost">
              <span title={r.localPaidNote ?? undefined}>Segnata incassata qui, su FattureInCloud ancora aperta</span>
            </Chip>
          </div>
        )}
      </td>
      {showRata && <td data-m={rata ? 'key' : 'off'} data-l="Rata" className="mute">{rata ?? ''}</td>}
      <td data-m="key" data-l="Incassata il" className="mono mute">{stornata ? '' : fmtDate(r.paidDate)}</td>
      <td data-m="key" data-l="Pagata con" className="mute">
        {stornata ? <Chip tone="ghost" dot>Stornata su FattureInCloud</Chip> : r.method ? METODO_LABEL[r.method] ?? r.method : '—'}
      </td>
      <td data-m="key" data-l="Importo" className="num mono font-semibold" style={{ whiteSpace: 'nowrap' }}>€ {fmtEur(r.amount, true)}</td>
      <td data-m="row" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end">
          {canWrite && !stornata && (
            <button className="btn btn-sm btn-ghost" disabled={unmarkPaid.isPending} title="L'incasso era sbagliato: la rata torna fra quelle da incassare"
              onClick={() => unmarkPaid.mutate({ paymentId: r.id, label: etichettaRata(r) })}>
              <Icon name="undo" size={10} /> Rimetti da incassare
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

/** Riga di una fattura tolta dall'elenco: perché, quando, e come farla tornare. */
function RigaTolta({ r, canWrite }: { r: IncassoRow; canWrite: boolean }) {
  const { restoreDocs } = useAdminActions();
  return (
    <tr style={{ cursor: 'default' }}>
      <td data-m="hd"><ClienteFattura r={r} /></td>
      <td data-m="key" data-l="Tolta il" className="mono mute">{fmtDate(r.ignoredAt)}</td>
      <td data-m="row" data-l="Perché è stata tolta">
        <div>{r.ignoredReason ? IGNORE_REASON_LABEL[r.ignoredReason] ?? r.ignoredReason : '—'}</div>
        {r.ignoredNote && <div className="text-[12px] mute">{r.ignoredNote}</div>}
      </td>
      <td data-m="key" data-l="Importo" className="num mono font-semibold mute" style={{ whiteSpace: 'nowrap' }}>€ {fmtEur(r.amount, true)}</td>
      <td data-m="row" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end">
          {canWrite && (
            <button className="btn btn-sm btn-ghost" disabled={restoreDocs.isPending}
              title="Rimettila fra le fatture" onClick={() => restoreDocs.mutate({ kind: 'invoice', ids: [r.invoiceId], label: etichettaRata(r) })}>
              <Icon name="undo" size={10} /> Rimetti nell'elenco
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
